import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Scales, MapPin, Phone, X, PaperPlaneRight, Plus, Paperclip, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import MultiImageUploader from "@/components/MultiImageUploader";
import { Link } from "react-router-dom";

export default function Lawyers() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [lawyers, setLawyers] = useState([]);
  const [tab, setTab] = useState("directory");
  const [consults, setConsults] = useState([]);
  const [activeConsult, setActiveConsult] = useState(null);
  const [showLawyerForm, setShowLawyerForm] = useState(false);
  const [showConsultForm, setShowConsultForm] = useState(false);
  const [myProfile, setMyProfile] = useState(null);

  const loadCats = async () => {
    try { const { data } = await api.get("/legal/categories"); setCategories(data); } catch (err) { console.error("load categories failed", err); }
  };
  const loadLawyers = async () => {
    const params = {}; if (category) params.category = category;
    try { const { data } = await api.get("/lawyer-listings", { params }); setLawyers(data); } catch (err) { console.error("load lawyers failed", err); toast.error("Failed to load lawyers"); }
  };
  const loadMine = async () => {
    if (!user || user === false) return;
    try { const { data } = await api.get("/legal-consultations/mine"); setConsults(data); } catch (err) { console.error("load consults failed", err); }
  };

  useEffect(() => { loadCats(); }, []);
  useEffect(() => { loadLawyers(); /* eslint-disable-next-line */ }, [category]);
  useEffect(() => { loadMine(); /* eslint-disable-next-line */ }, [user, tab]);

  useEffect(() => {
    if (!user || user === false) { setMyProfile(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/lawyer-listings");
        const mine = data.find((d) => d.user_id === user.id) || null;
        setMyProfile(mine);
      } catch (err) { console.error("load my lawyer profile failed", err); }
    })();
  }, [user]);

  const filteredLawyers = q
    ? lawyers.filter((l) => (l.full_name + " " + l.specialization + " " + l.city).toLowerCase().includes(q.toLowerCase()))
    : lawyers;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#2C4A3B]">Community legal aid</div>
          <h1 className="font-heading text-5xl mt-2 tracking-tight">Ask an <em className="text-[#2C4A3B] not-italic">advocate</em>. Openly.</h1>
          <p className="text-[#5C564D] mt-2 max-w-2xl">Post a legal query in the right area of law. Every advocate online can read, reply, ask for documents, or invite you to their office.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input data-testid="law-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name / specialty" className="px-3 py-2 rounded-full border border-[#E5D9CC] bg-[#F9F6F0] text-sm w-64" />
          {user && user !== false && !myProfile && (
            <button data-testid="btn-lawyer-signup" onClick={() => setShowLawyerForm(true)} className="samaaj-btn-outline text-sm">I&apos;m a lawyer</button>
          )}
          {user && user !== false && (
            <button data-testid="btn-new-legal-consult" onClick={() => setShowConsultForm(true)} className="samaaj-btn-primary text-sm flex items-center gap-1"><Plus size={14} weight="bold" /> Ask now</button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="legal-categories">
        <CategoryTile label="All" onClick={() => setCategory("")} active={!category} />
        {categories.map((c) => (
          <CategoryTile key={c.id} label={c.label} onClick={() => setCategory(c.id)} active={category === c.id} />
        ))}
      </div>

      <div className="mt-8 flex gap-2 border-b border-[#E5D9CC]">
        {[["directory", "Lawyers"], ["consults", "Consultations"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`law-tab-${k}`} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-[#2C4A3B] text-[#2C4A3B]" : "border-transparent text-[#5C564D]"}`}>{l}</button>
        ))}
      </div>

      {tab === "directory" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="lawyers-grid">
          {filteredLawyers.length === 0
            ? <div className="samaaj-card p-10 col-span-full text-center text-[#5C564D]">No advocates listed for this filter.</div>
            : filteredLawyers.map((d) => (
              <div key={d.id} className="samaaj-card p-5" data-testid={`law-${d.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-[#F2EBE5] border border-[#E5D9CC] flex items-center justify-center shrink-0">
                    {d.photo_url ? <img src={d.photo_url} alt={d.full_name} className="w-full h-full object-cover" /> : <Scales size={22} weight="duotone" className="text-[#2C4A3B]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-lg text-[#1C1B1A] truncate">Adv. {d.full_name}</div>
                    <div className="text-xs text-[#5C564D] truncate">{d.specialization}{d.years_experience ? ` · ${d.years_experience} yrs` : ""}</div>
                  </div>
                  {d.is_online_now && <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" title="Online now" />}
                </div>
                <p className="text-sm text-[#5C564D] mt-3 line-clamp-3 min-h-[48px]">{d.bio || "Available for legal consultation with the community."}</p>
                {d.categories?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {d.categories.slice(0, 4).map((c) => (<span key={c} className="samaaj-chip text-[10px]">{c}</span>))}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-3 text-xs text-[#5C564D]">
                  <span className="flex items-center gap-1"><MapPin size={12} /> {d.city}</span>
                  {d.bar_council_id && <span className="text-[10px]">Bar: {d.bar_council_id}</span>}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="font-heading text-lg text-[#D95A41]">${d.consultation_fee || 0} <span className="text-xs text-[#5C564D]">/ consult</span></div>
                  {d.contact_phone && <a href={`tel:${d.contact_phone}`} className="samaaj-btn-outline text-xs flex items-center gap-1"><Phone size={12} /> Call</a>}
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === "consults" && (
        <LegalConsultationsList consults={consults} user={user} onOpen={setActiveConsult} />
      )}

      {showLawyerForm && <LawyerForm categories={categories} onClose={() => setShowLawyerForm(false)} onDone={() => { setShowLawyerForm(false); toast.success("Submitted — pending admin review"); }} />}
      {showConsultForm && <LegalConsultForm categories={categories} onClose={() => setShowConsultForm(false)} onDone={(c) => { setShowConsultForm(false); loadMine(); setTab("consults"); setActiveConsult(c); }} />}
      {activeConsult && <LegalThread cid={activeConsult.id} user={user} myProfile={myProfile} onClose={() => { setActiveConsult(null); loadMine(); }} />}
    </div>
  );
}

function CategoryTile({ label, active, onClick }) {
  return (
    <button onClick={onClick} className={`p-3 rounded-2xl border text-xs font-semibold ${active ? "bg-[#2C4A3B] text-[#F9F6F0] border-[#2C4A3B]" : "bg-[#F2EBE5] border-[#E5D9CC] text-[#2C4A3B] hover:bg-[#EFE4D3]"}`}>
      {label}
    </button>
  );
}

function LegalConsultationsList({ consults, user, onOpen }) {
  if (!user || user === false) return <div className="mt-6 samaaj-card p-8 text-center text-[#5C564D]">Log in to see or start legal consultations.</div>;
  if (consults.length === 0) return <div className="mt-6 samaaj-card p-8 text-center text-[#5C564D]">No legal consultations yet. Tap “Ask now” to post your first query.</div>;
  return (
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="legal-consults-list">
      {consults.map((c) => {
        const msgs = c.messages || [];
        const last = msgs[msgs.length - 1];
        return (
          <button key={c.id} onClick={() => onOpen(c)} data-testid={`legal-consult-${c.id}`} className="samaaj-card p-5 text-left hover:border-[#2C4A3B]">
            <div className="flex items-center justify-between">
              <span className="samaaj-chip text-[10px] uppercase">{c.category}</span>
              <span className={`text-[10px] font-bold ${c.status === "answered" ? "text-emerald-700" : "text-[#D49A36]"}`}>{c.status}</span>
            </div>
            <div className="font-heading text-lg mt-2 text-[#1C1B1A] line-clamp-1">{c.title}</div>
            <div className="text-xs text-[#5C564D] mt-1">by {c.client_name}</div>
            <div className="text-sm text-[#1C1B1A] mt-2 line-clamp-2">{last?.text || c.story}</div>
            <div className="mt-2 text-[10px] text-[#5C564D]">{msgs.length} message{msgs.length !== 1 ? "s" : ""}</div>
          </button>
        );
      })}
    </div>
  );
}

function LawyerForm({ categories, onClose, onDone }) {
  const [f, setF] = useState({ full_name: "", specialization: "", categories: [], languages: "", city: "", consultation_mode: "both", consultation_fee: 20, years_experience: 5, bio: "", contact_phone: "", bar_council_id: "" });
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setF((p) => ({ ...p, categories: p.categories.includes(id) ? p.categories.filter(x => x !== id) : [...p.categories, id] }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/lawyer-listings", {
        ...f,
        consultation_fee: Number(f.consultation_fee),
        years_experience: Number(f.years_experience),
        languages: f.languages.split(",").map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-lg w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div className="font-heading text-3xl">List yourself</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3">
          <input required placeholder="Adv. Full Name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="law-name" className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input required placeholder="Specialization (e.g. Family Law)" value={f.specialization} onChange={(e) => setF({ ...f, specialization: e.target.value })} data-testid="law-spec" className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <div className="col-span-2">
            <div className="text-xs text-[#5C564D] mb-2">Areas of practice</div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button type="button" key={c.id} onClick={() => toggle(c.id)} data-testid={`law-cat-${c.id}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${f.categories.includes(c.id) ? "bg-[#2C4A3B] text-[#F9F6F0] border-[#2C4A3B]" : "bg-[#F2EBE5] text-[#2C4A3B] border-[#E5D9CC]"}`}>{c.label}</button>
              ))}
            </div>
          </div>
          <input required placeholder="Chamber city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input type="number" placeholder="Years exp" value={f.years_experience} onChange={(e) => setF({ ...f, years_experience: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input placeholder="Bar Council ID" value={f.bar_council_id} onChange={(e) => setF({ ...f, bar_council_id: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input type="number" step="0.01" placeholder="Consultation fee (USD)" value={f.consultation_fee} onChange={(e) => setF({ ...f, consultation_fee: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input placeholder="Phone" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <textarea rows={3} placeholder="Short bio" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <button data-testid="law-submit" disabled={busy} className="samaaj-btn-primary col-span-2">{busy ? "Sending…" : "Submit for review"}</button>
        </form>
      </div>
    </div>
  );
}

function LegalConsultForm({ categories, onClose, onDone }) {
  const [f, setF] = useState({ category: categories[0]?.id || "civil", title: "", story: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/legal-consultations", f);
      toast.success("Posted — advocates have been notified"); onDone(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-lg w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div className="font-heading text-3xl">Ask an advocate</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <select required value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="legal-cat" className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]">
            {categories.map((c) => (<option key={c.id} value={c.id}>{c.label}</option>))}
          </select>
          <input required maxLength={140} data-testid="legal-title" placeholder="Short title (e.g. Property mutation dispute)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <textarea required rows={6} data-testid="legal-story" placeholder="Describe the situation, dates, parties involved…" value={f.story} onChange={(e) => setF({ ...f, story: e.target.value })} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <button data-testid="legal-submit" disabled={busy} className="samaaj-btn-primary w-full">{busy ? "Posting…" : "Post consultation"}</button>
        </form>
      </div>
    </div>
  );
}

function LegalThread({ cid, user, myProfile, onClose }) {
  const [consult, setConsult] = useState(null);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/legal-consultations/${cid}`); setConsult(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onClose(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);
  if (!consult) return null;
  const isClient = consult.client_id === user.id;
  const isLawyer = !!myProfile && (myProfile.categories || []).includes(consult.category);
  const messages = consult.messages && consult.messages.length ? consult.messages : [{ author_role: "client", author_name: consult.client_name, text: consult.story, kind: "text", attachments: [], created_at: consult.created_at }];

  const send = async (kind = "text") => {
    if (!text && attachments.length === 0 && kind === "text") { toast.error("Type a message or attach documents"); return; }
    setBusy(true);
    try {
      await api.post("/legal-consultations/message", { consultation_id: cid, text, kind, attachments });
      setText(""); setAttachments([]); setShowUpload(false); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendDocsRequest = async () => { setBusy(true); try { await api.post("/legal-consultations/message", { consultation_id: cid, text: text || "Please share the relevant documents.", kind: "docs_request", attachments: [] }); setText(""); await load(); toast.success("Documents requested"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const sendOfficeVisit = async () => { setBusy(true); try { await api.post("/legal-consultations/message", { consultation_id: cid, text: text || "Please visit my office for a detailed discussion.", kind: "office_visit", attachments: [] }); setText(""); await load(); toast.success("Client asked to visit office"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };
  const uploadDocs = async () => { if (attachments.length === 0) { toast.error("Add at least one document"); return; } setBusy(true); try { await api.post("/legal-consultations/message", { consultation_id: cid, text: text || "Uploaded documents.", kind: "docs_uploaded", attachments }); setText(""); setAttachments([]); setShowUpload(false); await load(); toast.success("Documents shared"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };

  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-2xl w-full p-6 bg-[#F9F6F0] my-8" data-testid={`legal-thread-${cid}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">{consult.category}</div>
            <div className="font-heading text-2xl mt-1">{consult.title}</div>
            <div className="text-xs text-[#5C564D] mt-1">by {consult.client_name}</div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/legal/thread/${cid}/print`} target="_blank" data-testid="btn-print-legal" className="samaaj-btn-outline text-xs flex items-center gap-1"><Printer size={12} /> Print</Link>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
          </div>
        </div>

        <div className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="legal-messages">
          {messages.map((m, i) => {
            const isMine = m.author_id === user.id;
            const isLaw = m.author_role === "lawyer";
            const bubble = isLaw ? "bg-[#2C4A3B]/10 border-[#2C4A3B]/30" : "bg-[#F2EBE5] border-[#E5D9CC]";
            const msgKey = m.id || m.created_at || `msg-${i}`;
            return (
              <div key={msgKey} className={`p-3 rounded-2xl border ${bubble} ${isMine ? "ml-6" : "mr-6"}`}>
                <div className="text-[10px] font-bold text-[#2C4A3B] flex items-center gap-2">
                  {isLaw ? `Adv. ${m.author_name}` : m.author_name}
                  {m.specialization && <span className="samaaj-chip text-[9px]">{m.specialization}</span>}
                  {m.kind === "docs_request" && <span className="samaaj-chip text-[9px] bg-[#D49A36]/20 text-[#7a5a10]">Docs requested</span>}
                  {m.kind === "docs_uploaded" && <span className="samaaj-chip text-[9px] bg-emerald-100 text-emerald-800">Docs</span>}
                  {m.kind === "office_visit" && <span className="samaaj-chip text-[9px] bg-[#D95A41]/20 text-[#8a2a15]">Office visit</span>}
                </div>
                {m.text && <div className="text-sm text-[#1C1B1A] mt-1 whitespace-pre-wrap">{m.text}</div>}
                {m.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {m.attachments.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer" className="block w-20 h-20 rounded-lg overflow-hidden border border-[#E5D9CC]">
                        <img src={url} alt="doc" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
                <div className="text-[10px] text-[#5C564D] mt-1">{new Date(m.created_at).toLocaleString()}</div>
              </div>
            );
          })}
        </div>

        {consult.docs_requested && isClient && !showUpload && (
          <div className="mt-3 p-3 rounded-2xl bg-[#D49A36]/15 border border-[#D49A36]/40 text-sm text-[#7a5a10] flex items-center justify-between gap-2">
            <span>Your advocate has asked for documents.</span>
            <button onClick={() => setShowUpload(true)} className="samaaj-btn-primary text-xs">Upload now</button>
          </div>
        )}
        {showUpload && isClient && (
          <div className="mt-3 p-3 rounded-2xl border border-[#E5D9CC] bg-[#F2EBE5]">
            <div className="text-xs font-bold text-[#2C4A3B] mb-2">Upload your documents</div>
            <MultiImageUploader value={attachments} onChange={setAttachments} max={8} testid="docs" label="Documents (photos/scans)" />
            <div className="mt-2 flex gap-2">
              <button disabled={busy || attachments.length === 0} onClick={uploadDocs} data-testid="btn-upload-docs" className="samaaj-btn-primary text-sm">Send documents</button>
              <button onClick={() => { setShowUpload(false); setAttachments([]); }} className="samaaj-btn-outline text-sm">Cancel</button>
            </div>
          </div>
        )}

        {(isClient || isLawyer) && (
          <div className="mt-4 border-t border-[#E5D9CC] pt-3">
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} data-testid="legal-input" placeholder={isClient ? "Reply, ask a follow-up…" : "Add a legal note…"} className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC] bg-white" />
            <div className="mt-2 flex flex-wrap gap-2 justify-end">
              {isClient && !showUpload && <button onClick={() => setShowUpload(true)} data-testid="btn-attach-legal" className="samaaj-btn-outline text-xs flex items-center gap-1"><Paperclip size={12} /> Attach documents</button>}
              {isLawyer && !isClient && (
                <>
                  <button disabled={busy} onClick={sendDocsRequest} data-testid="btn-request-docs" className="samaaj-btn-outline text-xs">Request documents</button>
                  <button disabled={busy} onClick={sendOfficeVisit} data-testid="btn-office-visit" className="samaaj-btn-outline text-xs">Invite to office</button>
                </>
              )}
              <button disabled={busy || !text} onClick={() => send("text")} data-testid="btn-send-legal" className="samaaj-btn-primary text-xs flex items-center gap-1"><PaperPlaneRight size={12} /> Send</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
